<script setup>
import { onMounted, ref } from 'vue';
import SimpleTagPicker from 'dashboard/components-next/simple-tag-picker/SimpleTagPicker.vue';

const images = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const message = ref('');
const fileInput = ref(null);
const form = ref(emptyForm());

const tagSuggestions = [
  { value: 'entradas', label: 'Entradas' },
  { value: 'topo', label: 'Topo da cabeca' },
  { value: 'linha-frontal', label: 'Linha frontal' },
  { value: 'grisalho', label: 'Cabelos grisalhos' },
  { value: 'naturalidade', label: 'Naturalidade' },
  { value: 'primeira-aplicacao', label: 'Primeira aplicacao' },
];

function emptyForm() {
  return {
    title: '',
    description: '',
    tags: [],
    sortOrder: 0,
    isActive: true,
    file: null,
  };
}

async function load() {
  isLoading.value = true;
  message.value = '';
  try {
    const response = await fetch('/api/superwoot/result-images');
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Falha ao carregar resultados');
    images.value = payload.resultImages || [];
  } catch (error) {
    message.value = error.message;
  } finally {
    isLoading.value = false;
  }
}

function chooseFile(event) {
  form.value.file = event.target.files?.[0] || null;
}

async function upload() {
  if (!form.value.file) {
    message.value = 'Escolha uma imagem.';
    return;
  }
  isSaving.value = true;
  message.value = '';
  try {
    const body = new FormData();
    Object.entries(form.value).forEach(([key, value]) => body.append(key, value));
    const response = await fetch('/api/superwoot/result-images', { method: 'POST', body });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Falha ao enviar imagem');
    form.value = emptyForm();
    if (fileInput.value) fileInput.value.value = '';
    message.value = 'Resultado adicionado.';
    await load();
  } catch (error) {
    message.value = error.message;
  } finally {
    isSaving.value = false;
  }
}

async function update(item, changes) {
  message.value = '';
  try {
    const response = await fetch('/api/superwoot/result-images', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resultImageId: item.id, ...changes }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Falha ao atualizar imagem');
    await load();
  } catch (error) {
    message.value = error.message;
  }
}

async function remove(item) {
  if (!window.confirm(`Remover "${item.title}"?`)) return;
  message.value = '';
  try {
    const response = await fetch(`/api/superwoot/result-images?resultImageId=${item.id}`, { method: 'DELETE' });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Falha ao remover imagem');
    await load();
  } catch (error) {
    message.value = error.message;
  }
}

onMounted(load);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-n-background">
    <header class="border-b border-n-weak px-4 py-4 sm:px-6">
      <h1 class="text-xl font-medium text-n-slate-12">Galeria de resultados</h1>
      <p class="mt-1 text-sm text-n-slate-10">Imagens reais que o atendimento envia quando o lead pede exemplos.</p>
    </header>

    <main class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
      <div class="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[320px_1fr]">
        <form class="space-y-4 rounded-md border border-n-weak p-4" @submit.prevent="upload">
          <div>
            <h2 class="text-base font-medium text-n-slate-12">Adicionar resultado</h2>
            <p class="mt-1 text-sm text-n-slate-10">Use imagens autorizadas para divulgação.</p>
          </div>
          <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Titulo</span><input v-model="form.title" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Resultado linha frontal" /></label>
          <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Descricao comercial</span><textarea v-model="form.description" rows="3" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="Acabamento natural e adaptado ao perfil do cliente." /></label>
          <div><span class="mb-1 block text-sm text-n-slate-11">Quando mostrar esta foto?</span><SimpleTagPicker v-model="form.tags" :suggestions="tagSuggestions" helper="Escolha os assuntos que mais combinam com o resultado." /></div>
          <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Ordem</span><input v-model.number="form.sortOrder" type="number" min="0" max="10000" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
          <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Imagem</span><input ref="fileInput" required accept="image/jpeg,image/png,image/webp" type="file" class="block w-full text-sm text-n-slate-11" @change="chooseFile" /></label>
          <label class="flex items-center gap-2 text-sm text-n-slate-11"><input v-model="form.isActive" type="checkbox" /> Disponivel para envio automatico</label>
          <button class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white disabled:opacity-60" type="submit" :disabled="isSaving">
            <span class="i-lucide-upload size-4" />
            {{ isSaving ? 'Enviando...' : 'Adicionar imagem' }}
          </button>
        </form>

        <section>
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-base font-medium text-n-slate-12">Resultados cadastrados</h2>
            <span class="text-sm text-n-slate-10">{{ images.length }} imagem(ns)</span>
          </div>
          <p v-if="message" class="mt-3 text-sm text-n-slate-11">{{ message }}</p>
          <p v-if="isLoading" class="mt-4 text-sm text-n-slate-10">Carregando...</p>
          <p v-else-if="images.length === 0" class="mt-4 text-sm text-n-slate-10">Nenhuma imagem cadastrada.</p>
          <div v-else class="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <article v-for="item in images" :key="item.id" class="overflow-hidden rounded-md border border-n-weak">
              <div class="aspect-[4/3] bg-n-alpha-2">
                <img :src="item.preview_url" :alt="item.title" class="h-full w-full object-cover" />
              </div>
              <div class="space-y-2 p-3">
                <div class="flex items-start justify-between gap-2">
                  <strong class="text-sm text-n-slate-12">{{ item.title }}</strong>
                  <button type="button" title="Remover imagem" class="i-lucide-trash-2 size-4 shrink-0 text-n-ruby-9" @click="remove(item)" />
                </div>
                <p v-if="item.description" class="text-xs text-n-slate-10">{{ item.description }}</p>
                <p v-if="item.tags?.length" class="text-xs text-n-slate-10">{{ item.tags.join(', ') }}</p>
                <label class="flex items-center gap-2 text-xs text-n-slate-11"><input :checked="item.is_active" type="checkbox" @change="update(item, { isActive: $event.target.checked })" /> Envio automatico</label>
              </div>
            </article>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>
